import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { ReactFlow, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import BracketsNode from './BracketsNode';
import BracketsTitle from './BracketsTitle';
import Select from './Select';
import { useFirebase } from './FirebaseContextProvider';
import { timeStr, calcBracketDepth, getTeamDivision } from '../utils/misc';
import { useDivisions } from '../utils/hooks';
import { PlayoffRoundOrder, roundIndex } from '../utils/enums';
import { storageDate } from '../utils/consts';
import './Brackets.scss';

const nodeTypes = { customNode: BracketsNode, titleNode: BracketsTitle };

const horizontalSpacing = 420;
const verticalSpacing = 140;

function generateTreeData({
  maxLevels,
  maxRound,
  referees,
  teams,
  games,
  fields,
  highlightRound,
  highlightPlacement,
  pickMode,
  coachesByTeamId,
  activeHoverKey,
  onHoverChange,
  onPick,
  onWatchInSchedule,
  bracketStartRound,
  prelimGames,
}) {
  const nodes = [];
  const edges = [];
  let idCounter = 0;

  function getId() {
    return `node-${idCounter++}`;
  }

  // Determine if a first-round position is a bye based on bracket structure.
  // Byes go to top children (odd placements) first, then bottom children
  // of higher-numbered parents when there are more byes than parents.
  const numFirstRoundParents = maxLevels >= 2 ? 2 ** (maxLevels - 2) : 0;
  function isByePosition(round, placement) {
    if (prelimGames === 0 || round !== bracketStartRound) return false;
    const parentIdx = Math.ceil(placement / 2);
    if (placement % 2 === 1) {
      return parentIdx > Math.max(0, prelimGames - numFirstRoundParents);
    }
    return parentIdx > prelimGames;
  }

  const getTeamNameById = (id) => teams.get(id)?.name;
  const getTeamThumbById = (id) => teams.get(id)?.imgThumbUrl;

  // A nameless coach carries an id for the busy checks, but the node would
  // draw it as an empty paragraph with an empty hover trigger
  const getCoachesById = (id) =>
    (coachesByTeamId.get(id) || []).filter((coach) => coach.name);

  function addNode(x, y, game, placement) {
    const id = getId();
    const { team1, team2, score1, score2, date, start, duration, field } =
      game || {};

    const getRefNameById = (id) => referees.get(id)?.name;

    nodes.push({
      id,
      position: { x, y },
      data: {
        gameId: game?.id,
        gameDate: game?.date,
        onWatchInSchedule,
        placement,
        team1: getTeamNameById(team1),
        team2: getTeamNameById(team2),
        team1Img: getTeamThumbById(team1),
        team2Img: getTeamThumbById(team2),
        team1Id: team1,
        team2Id: team2,
        team1Coaches: getCoachesById(team1),
        team2Coaches: getCoachesById(team2),
        score1,
        score2,
        date,
        time: timeStr(start, duration),
        field: fields.find((f) => f.id === field)?.name || '',
        referees:
          game?.referees.map((ref) => ({
            id: ref,
            name: getRefNameById(ref),
          })) || [],
        highlight:
          !!game &&
          game.round === highlightRound &&
          game.placement === highlightPlacement,
        activeHoverKey,
        onHoverChange,
      },
      type: 'customNode',
    });
    return id;
  }

  function addByeNode(
    x,
    y,
    placement,
    byeTeamName,
    byeTeamImg,
    byeTeamId,
    byeTeamCoaches,
  ) {
    const id = getId();
    nodes.push({
      id,
      position: { x, y },
      data: {
        bye: true,
        placement,
        byeTeamName: byeTeamName || null,
        byeTeamImg: byeTeamImg || null,
        byeTeamId: byeTeamId || null,
        byeTeamCoaches: byeTeamCoaches || [],
        activeHoverKey,
        onHoverChange,
      },
      type: 'customNode',
    });
    return id;
  }

  function addFreeNode(x, y, round, placement) {
    const id = getId();
    const isHighlighted =
      highlightRound === round && highlightPlacement === placement;
    nodes.push({
      id,
      position: { x, y },
      data: {
        free: true,
        placement: placement - 1,
        highlight: isHighlighted,
        pickMode,
        onClick: onPick ? () => onPick({ round, placement }) : undefined,
      },
      type: 'customNode',
    });
    return id;
  }

  const rootGame = games.find((g) => g.round === maxRound && g.placement === 1);
  const rootId = !rootGame
    ? addFreeNode(0, 0, maxRound, 1)
    : addNode(0, 0, rootGame, 0);

  function generateSubtree({
    parentId,
    parentPos,
    parentX,
    parentY,
    currentLevel,
    maxLevel,
    isLeftBranch,
    leftRightBranches,
  }) {
    if (currentLevel >= maxLevel - 1) return;

    const childXTop =
      isLeftBranch || leftRightBranches
        ? parentX - horizontalSpacing
        : parentX + horizontalSpacing;
    const childXBottom =
      !isLeftBranch || leftRightBranches
        ? parentX + horizontalSpacing
        : parentX - horizontalSpacing;
    const childYTop = leftRightBranches
      ? parentY
      : parentY - verticalSpacing * 2 ** (maxLevel - currentLevel - 2);
    const childYBottom = leftRightBranches
      ? parentY
      : parentY + verticalSpacing * 2 ** (maxLevel - currentLevel - 2);

    const roundChild =
      PlayoffRoundOrder[roundIndex(maxRound) - (currentLevel + 1)];
    const parentPlacement = parentPos + 1; // 1-based

    // Find parent game for BYE team name lookup
    const roundParent = PlayoffRoundOrder[roundIndex(maxRound) - currentLevel];
    const parentGame = games.find(
      (g) => g.round === roundParent && g.placement === parentPlacement,
    );

    const childGames = games
      .filter(
        (g) =>
          g.round === roundChild &&
          (g.placement === parentPos * 2 + 1 ||
            g.placement === parentPos * 2 + 2),
      )
      .sort((a, b) => a.placement - b.placement);

    const childRoundExists = games.some((g) => g.round === roundChild);
    if (!childRoundExists && !pickMode && !bracketStartRound) return;

    let topGame, bottomGame;
    if (childGames.length >= 2) {
      topGame = childGames[0];
      bottomGame = childGames[1];
    } else if (childGames.length === 1) {
      // In prelim rounds with BYEs: even placement → bottom, odd → top
      if (childGames[0].placement % 2 === 0) {
        topGame = null;
        bottomGame = childGames[0];
      } else {
        topGame = childGames[0];
        bottomGame = null;
      }
    } else {
      topGame = null;
      bottomGame = null;
    }

    // --- Top child ---
    const topPlacement = parentPos * 2 + 1;
    const topChildPos = topGame ? topGame.placement - 1 : parentPos * 2;
    const topIsBye = isByePosition(roundChild, topPlacement);
    let topChildId;
    if (topGame) {
      topChildId = addNode(childXTop, childYTop, topGame, topChildPos);
    } else if (topIsBye) {
      topChildId = addByeNode(
        childXTop,
        childYTop,
        parentPos * 2,
        parentGame ? getTeamNameById(parentGame.team1) : null,
        parentGame ? getTeamThumbById(parentGame.team1) : null,
        parentGame?.team1,
        parentGame?.team1 ? getCoachesById(parentGame.team1) : [],
      );
    } else {
      topChildId = addFreeNode(childXTop, childYTop, roundChild, topPlacement);
    }

    edges.push({
      id: `edge-${parentId}-${topChildId}`,
      source: parentId,
      target: topChildId,
      sourceHandle:
        isLeftBranch || leftRightBranches ? 'left-output' : 'right-output',
      targetHandle:
        isLeftBranch || leftRightBranches ? 'right-input' : 'left-input',
    });

    if (topGame || pickMode || bracketStartRound) {
      generateSubtree({
        parentId: topChildId,
        parentPos: topChildPos,
        parentX: childXTop,
        parentY: childYTop,
        currentLevel: currentLevel + 1,
        maxLevel,
        isLeftBranch: leftRightBranches ? true : isLeftBranch,
      });
    }

    // --- Bottom child ---
    const bottomPlacement = parentPos * 2 + 2;
    const bottomChildPos = bottomGame
      ? bottomGame.placement - 1
      : parentPos * 2 + 1;
    const bottomIsBye = isByePosition(roundChild, bottomPlacement);
    let bottomChildId;
    if (bottomGame) {
      bottomChildId = addNode(
        childXBottom,
        childYBottom,
        bottomGame,
        bottomChildPos,
      );
    } else if (bottomIsBye) {
      bottomChildId = addByeNode(
        childXBottom,
        childYBottom,
        parentPos * 2 + 1,
        parentGame ? getTeamNameById(parentGame.team2) : null,
        parentGame ? getTeamThumbById(parentGame.team2) : null,
        parentGame?.team2,
        parentGame?.team2 ? getCoachesById(parentGame.team2) : [],
      );
    } else {
      bottomChildId = addFreeNode(
        childXBottom,
        childYBottom,
        roundChild,
        bottomPlacement,
      );
    }

    edges.push({
      id: `edge-${parentId}-${bottomChildId}`,
      source: parentId,
      target: bottomChildId,
      sourceHandle:
        !isLeftBranch || leftRightBranches ? 'right-output' : 'left-output',
      targetHandle:
        !isLeftBranch || leftRightBranches ? 'left-input' : 'right-input',
    });

    if (bottomGame || pickMode || bracketStartRound) {
      generateSubtree({
        parentId: bottomChildId,
        parentPos: bottomChildPos,
        parentX: childXBottom,
        parentY: childYBottom,
        currentLevel: currentLevel + 1,
        maxLevel,
        isLeftBranch: leftRightBranches ? false : isLeftBranch,
      });
    }
  }

  generateSubtree({
    parentId: rootId,
    parentPos: 0,
    parentX: 0,
    parentY: 0,
    currentLevel: 0,
    maxLevel: maxLevels,
    leftRightBranches: true,
  });

  function generateTitles() {
    const usedLabels = Array.from(
      { length: maxLevels },
      (_, i) => PlayoffRoundOrder[roundIndex(maxRound) - i] ?? `R${i}`,
    );
    const centerLabel = usedLabels[0];
    const sideLabels = usedLabels.slice(1);
    const leftSide = [...sideLabels].reverse();
    const finalTitles = [...leftSide, centerLabel, ...sideLabels];

    const minY = Math.min(...nodes.map((node) => node.position.y));
    const y = minY - verticalSpacing;
    const x = finalTitles.map(
      (_, i) => (i - (maxLevels - 1)) * horizontalSpacing,
    );

    finalTitles.forEach((title, i) => {
      nodes.push({
        id: `title-${i}`,
        position: { x: x[i], y },
        data: { title },
        type: 'titleNode',
      });
    });
  }

  generateTitles();

  return { nodes, edges };
}

export default function Brackets({
  id,
  fields,
  highlightRound,
  highlightPlacement,
  onPick,
  initDivision,
}) {
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [activeHoverKey, setActiveHoverKey] = useState(null);
  const handleHoverChange = useCallback((key) => setActiveHoverKey(key), []);
  const divisions = useDivisions(id);
  const [division, setDivision] = useState(null);
  const pickMode = !!onPick;
  const navigate = useNavigate();
  const { refereesById, teamsById, tournamentsById, coachesByTeamId } =
    useFirebase();
  const tournament = tournamentsById.get(id);
  const { games, teams: tournamentTeams } = tournament;

  const handleWatchInSchedule = useCallback(
    (gameId, gameDate) => {
      localStorage.setItem(storageDate, moment(gameDate).format('YYYY-MM-DD'));
      navigate('?highlight=' + gameId + '#schedule', { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    if (divisions.length > 0) setDivision(initDivision || divisions[0]);
  }, [divisions, initDivision]);

  const { nodes, edges } = useMemo(() => {
    const filteredGames = games.filter((game) => game.division === division);
    // Pool games have round == null; only games with a playoff round populate
    // the bracket. A division can have pool games but no playoff games yet.
    const playoffGames = filteredGames.filter((g) => g.round != null);

    // Compute full bracket depth from team count
    const teamCount = (tournamentTeams || [])
      .filter((t) => t.admitted)
      .map((ref) => teamsById.get(ref.id))
      .filter(Boolean)
      .filter((team) => getTeamDivision(team) === division).length;
    const { numRounds, startRound, prelimGames } = calcBracketDepth(teamCount);

    if (playoffGames.length === 0) {
      // Only draw the interactive placeholder skeleton for the "Choose on
      // brackets" picker. In view mode, show a single "Isn't scheduled" label.
      if (numRounds > 0 && pickMode) {
        return generateTreeData({
          maxLevels: numRounds,
          maxRound: 'F',
          referees: refereesById,
          teams: teamsById,
          games: [],
          fields,
          highlightRound,
          highlightPlacement,
          pickMode,
          coachesByTeamId,
          activeHoverKey,
          onHoverChange: handleHoverChange,
          onPick,
          onWatchInSchedule: !initDivision ? handleWatchInSchedule : undefined,
          bracketStartRound: startRound,
          prelimGames,
        });
      }
      return {
        nodes: [
          {
            id: 'no-scheduled',
            position: { x: 0, y: 0 },
            data: { title: "Isn't scheduled" },
            type: 'titleNode',
          },
        ],
        edges: [],
      };
    }

    const roundIndices = filteredGames
      .filter((g) => g.round != null)
      .map((g) => roundIndex(g.round));
    const maxIdx = Math.max(...roundIndices);
    const maxRound = numRounds > 0 ? 'F' : PlayoffRoundOrder[maxIdx];
    const levels =
      numRounds > 0 ? numRounds : maxIdx - Math.min(...roundIndices) + 1;

    return generateTreeData({
      maxLevels: levels,
      maxRound,
      referees: refereesById,
      teams: teamsById,
      games: filteredGames,
      fields,
      highlightRound,
      highlightPlacement,
      pickMode,
      coachesByTeamId,
      activeHoverKey,
      onHoverChange: handleHoverChange,
      onPick,
      onWatchInSchedule: !initDivision ? handleWatchInSchedule : undefined,
      bracketStartRound: startRound,
      prelimGames,
    });
  }, [
    games,
    division,
    refereesById,
    teamsById,
    fields,
    highlightRound,
    highlightPlacement,
    pickMode,
    initDivision,
    coachesByTeamId,
    activeHoverKey,
    handleHoverChange,
    onPick,
    handleWatchInSchedule,
    tournamentTeams,
  ]);

  const containerRef = useRef(null);

  const syncD3Zoom = () => {
    const container = containerRef.current;
    if (!container) return;
    const renderer = container.querySelector('.react-flow__renderer');
    const viewport = container.querySelector('.react-flow__viewport');
    if (!renderer?.__zoom || !viewport) return;
    const match = viewport.style.transform.match(
      /translate\(([-\d.]+)px,\s*([-\d.]+)px\)\s*scale\(([-\d.]+)\)/,
    );
    if (match) {
      renderer.__zoom.k = parseFloat(match[3]);
      renderer.__zoom.x = parseFloat(match[1]);
      renderer.__zoom.y = parseFloat(match[2]);
    }
  };

  useEffect(() => {
    if (!reactFlowInstance) return;
    const t1 = setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.1 });
    }, 50);
    const t2 = setTimeout(syncD3Zoom, 200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [division, nodes.length, edges.length, reactFlowInstance]);

  return (
    <div className='brackets' ref={containerRef}>
      {!initDivision && (
        <div className='controls'>
          <Select
            options={divisions}
            value={division}
            onChange={(val) => setDivision(val)}
            placeholder='Division'
            small
            className='division'
          />
        </div>
      )}
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={{ type: 'step' }}
          onInit={setReactFlowInstance}
        />
      </ReactFlowProvider>
    </div>
  );
}
